import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { ArrowLeft, Loader2, UserX } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlockedUsers() {
  const navigate = useNavigate();
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState(null);

  const headers = () => ({
    withCredentials: true,
    headers: { ...(localStorage.getItem('access_token') && { Authorization: `Bearer ${localStorage.getItem('access_token')}` }) }
  });

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/users/blocked`, headers());
        setBlockedUsers(res.data.blocked_users || []);
      } catch {
        toast.error('Error al cargar usuarios bloqueados');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleUnblock = async (userId, name) => {
    setUnblocking(userId);
    try {
      await axios.delete(`${API}/users/${userId}/block`, headers());
      setBlockedUsers(prev => prev.filter(u => u.user_id !== userId));
      toast.success(`${name} desbloqueado`);
    } catch {
      toast.error('Error al desbloquear');
    } finally {
      setUnblocking(null);
    }
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return 'https://images.unsplash.com/photo-1760551937527-2bc6cfe45180?w=400';
    if (photo.startsWith('http')) return photo;
    return `${API}/files/${photo}`;
  };

  return (
    <div className="min-h-screen bg-[hsl(35,25%,91%)]">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/profile')} className="flex items-center gap-2 text-[hsl(20,10%,40%)] hover:text-[hsl(20,10%,25%)] mb-6">
          <ArrowLeft className="w-4 h-4" />
          Volver al perfil
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-[hsl(35,20%,88%)] p-6">
          <h1 className="font-heading font-bold text-2xl text-gray-900 mb-2">Usuarios bloqueados</h1>
          <p className="text-sm text-gray-500 mb-6">Los usuarios bloqueados no pueden ver tu perfil ni contactarte.</p>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[hsl(144,18%,45%)]" />
            </div>
          ) : blockedUsers.length === 0 ? (
            <div className="text-center py-12">
              <UserX className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No tienes ningún usuario bloqueado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {blockedUsers.map(u => (
                <div key={u.user_id} className="flex items-center justify-between p-4 bg-[hsl(35,25%,95%)] rounded-xl border border-[hsl(35,20%,88%)]">
                  <div className="flex items-center gap-3">
                    <img src={getPhotoUrl(u.profile_photo)} alt={u.name} className="w-12 h-12 rounded-xl object-cover" />
                    <div>
                      <p className="font-medium text-gray-900">{u.name}{u.age ? `, ${u.age}` : ''}</p>
                      {u.city && <p className="text-xs text-gray-500">{u.city}</p>}
                    </div>
                  </div>
                  <button
                    onClick={() => handleUnblock(u.user_id, u.name)}
                    disabled={unblocking === u.user_id}
                    className="px-3 py-1.5 text-sm rounded-xl border border-[hsl(35,20%,75%)] text-[hsl(20,10%,35%)] hover:bg-[hsl(35,25%,85%)] transition-colors disabled:opacity-50"
                  >
                    {unblocking === u.user_id ? 'Desbloqueando...' : 'Desbloquear'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
